import { ChartLine, Download, Info } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { exportarCsv } from '@/lib/export';
import { formatNumber } from '@/lib/utils';

import { ConsolidadoRic } from '../components/consolidado-ric';
import { RicNav } from '../components/ric-nav';
import { useRicOsinerming } from '../hooks/use-ric';
import { alertaOsinerming, ANIOS, MESES, porcentaje } from '../lib/ric-format';

/** Mes y año por defecto. */
function periodoInicial(): { anio: number; mes: number } {
  const hoy = new Date();

  return { anio: hoy.getFullYear(), mes: hoy.getMonth() + 1 };
}

/**
 * RIC — Osinerming (`/monitoreo/ric/osinerming`).
 *
 * Puerto de `Reportes::ric_osinerming`: el mismo consolidado mensual pero
 * agrupado por **producto padre** (`pts_fuel_grades.parent_id`), con las
 * columnas de recepción/estación y las alertas `CHEQUEAR!`, `AYUDA` y `ALARMA`
 * que el panel calculaba en el navegador. Es el formato que se presenta al
 * regulador.
 */
export function RicOsinermingPage() {
  useDocumentTitle('RIC · Osinerming');

  const inicial = periodoInicial();
  const [anio, setAnio] = useState(inicial.anio);
  const [mes, setMes] = useState(inicial.mes);

  const osinerming = useRicOsinerming(anio, mes);
  const bloques = osinerming.data?.products ?? [];

  const exportar = () => {
    if (bloques.length === 0) return;

    const encabezados = [
      'Producto',
      'Tanques',
      'Día',
      'Previo',
      'Ingresos',
      'Salidas',
      'Teórica',
      'Físico',
      'Agua',
      'Físico sin agua',
      'Retornos',
      'Diferencia',
      'Recepción',
      'Estación',
      'Alerta',
    ];

    const filas = bloques.flatMap((bloque) =>
      bloque.rows.map((fila) => {
        const recepcion = fila.received - fila.incomes;
        const estacion = fila.returns;
        const desvio = porcentaje(fila.difference, fila.outcomes || fila.incomes || 1);

        return [
          bloque.product_label,
          (bloque.tank_ids ?? []).join(', '),
          fila.day,
          fila.previous.toFixed(3),
          fila.incomes.toFixed(3),
          fila.outcomes.toFixed(3),
          fila.logical.toFixed(3),
          fila.physical.toFixed(3),
          fila.water_gl.toFixed(3),
          fila.physical_no_water.toFixed(3),
          fila.returns.toFixed(3),
          fila.difference.toFixed(3),
          recepcion.toFixed(3),
          estacion.toFixed(3),
          alertaOsinerming(fila.outcomes, desvio),
        ];
      }),
    );

    exportarCsv(`ric-osinerming-${anio}-${String(mes).padStart(2, '0')}`, encabezados, filas);
  };

  const conAlerta = bloques.flatMap((bloque) =>
    bloque.rows.map((fila) => {
      const desvio = porcentaje(fila.difference, fila.outcomes || fila.incomes || 1);

      return alertaOsinerming(fila.outcomes, desvio);
    }),
  );

  const totalChequear = conAlerta.filter((alerta) => alerta === 'CHEQUEAR!').length;
  const totalAyuda = conAlerta.filter((alerta) => alerta === 'AYUDA').length;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Monitoreo y control
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <ChartLine className="size-6 text-brand-500" aria-hidden="true" />
            RIC · Osinerming
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Consolidado mensual por producto, con las columnas de recepción y las alertas de control.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <RicNav active="osinerming" />
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Download className="size-3.5" aria-hidden="true" />}
            onClick={exportar}
            disabled={bloques.length === 0}
          >
            Exportar
          </Button>
        </div>
      </header>

      <SectionCard
        title="Período"
        description={
          osinerming.data?.company_name
            ? `Informe de ${osinerming.data.company_name}`
            : 'Selecciona el mes a consolidar'
        }
      >
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
            Mes
            <Select
              value={String(mes)}
              onChange={(event) => setMes(Number(event.target.value))}
              className="h-10 rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
            >
              {MESES.map((nombre, indice) => (
                <option key={nombre} value={String(indice + 1)}>
                  {nombre}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
            Año
            <Select
              value={String(anio)}
              onChange={(event) => setAnio(Number(event.target.value))}
              className="h-10 rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
            >
              {ANIOS.map((valor) => (
                <option key={valor} value={String(valor)}>
                  {valor}
                </option>
              ))}
            </Select>
          </label>

          <Button
            size="sm"
            variant="outline"
            onClick={() => void osinerming.refetch()}
            loading={osinerming.isRefetching}
          >
            Consultar
          </Button>

          <span className="pb-2.5 text-xs text-ink-500">
            {osinerming.data
              ? `Del ${osinerming.data.start} al ${osinerming.data.end} · ${bloques.length} productos`
              : ''}
          </span>
        </div>
      </SectionCard>

      <div className="rounded-card border border-brand-100 bg-brand-50/60 px-4 py-3">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-brand-900">
          <Info className="size-3.5" aria-hidden="true" />
          Cómo se interpretan las alertas
        </p>
        <p className="mt-1 text-xs leading-relaxed text-brand-900/80">
          <span className="font-semibold">CHEQUEAR!</span> cuando las ventas del día son menores a
          3.500 gal o el desvío supera el 40 %; <span className="font-semibold">AYUDA</span> cuando el
          desvío pasa del 25 %; en el resto de los casos se muestra{' '}
          <span className="font-semibold">ALARMA</span>. La recepción es el volumen recibido menos
          los ingresos, y la estación son los retornos a tanque.
        </p>
      </div>

      {osinerming.isError ? (
        <Alert variant="error" title="No se pudo cargar el consolidado">
          <div className="flex flex-wrap items-center gap-3">
            <span>Verifica el período e inténtalo nuevamente.</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void osinerming.refetch()}
              loading={osinerming.isRefetching}
            >
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {bloques.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Productos
            </p>
            <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">
              {formatNumber(bloques.length)}
            </p>
            <p className="mt-0.5 text-xs text-ink-500">
              {bloques.reduce((total, bloque) => total + (bloque.tank_ids?.length ?? 0), 0)} tanques
            </p>
          </div>

          <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Días a chequear
            </p>
            <p
              className={
                totalChequear > 0
                  ? 'mt-1 font-mono text-lg font-bold tabular-nums text-rose-600'
                  : 'mt-1 font-mono text-lg font-bold tabular-nums text-ink-900'
              }
            >
              {formatNumber(totalChequear)}
            </p>
            <p className="mt-0.5 text-xs text-ink-500">Ventas bajas o desvío mayor al 40 %</p>
          </div>

          <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Días con ayuda
            </p>
            <p
              className={
                totalAyuda > 0
                  ? 'mt-1 font-mono text-lg font-bold tabular-nums text-amber-600'
                  : 'mt-1 font-mono text-lg font-bold tabular-nums text-ink-900'
              }
            >
              {formatNumber(totalAyuda)}
            </p>
            <p className="mt-0.5 text-xs text-ink-500">Desvío entre 25 % y 40 %</p>
          </div>

          <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Diferencia acumulada
            </p>
            <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">
              {formatNumber(
                bloques.reduce((total, bloque) => total + bloque.totals.difference, 0),
              )}{' '}
              gal
            </p>
            <p className="mt-0.5 text-xs text-ink-500">
              Umbral: {formatNumber(osinerming.data?.threshold ?? 100)} gal
            </p>
          </div>
        </div>
      ) : null}

      <SectionCard
        title="Consolidado por producto"
        description="Los tanques que comparten producto padre suman en una sola tabla"
      >
        <ConsolidadoRic
          data={osinerming.data}
          bloques={bloques}
          loading={osinerming.isPending}
          osinerming
          tituloBloque="Productos"
        />
      </SectionCard>
    </div>
  );
}
